import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "../../i18n/TranslationProvider";

import { useHistory } from "react-router-dom";

import { Modal } from "react-bootstrap/";
import SubmitButton from "../UIComponents/SubmitButton";
import Sallie from "../UIComponents/Sallie";
import PrivacyPage from "./PrivacyPage";
import OnboardingStepHeader from "./OnboardingStepHeader";
import LicenseKeyInput from "./LicenseKeyInput";

import { createCheckoutSession, getPaymentRecord, startFreeTrial } from "../../api/paymentRequest";

const PaymentPage = ({ user }) => {
  const { t } = useTranslation();
  const history = useHistory();

  useEffect(() => {
    if (!user) { history.push("/signIn"); }
  }, [user, history]);

  const [loading, setLoading] = useState(false);
  const [paid, setPaid] = useState(false);
  const [licenseKeys, setLicenseKeys] = useState([]);
  const [verified, setVerified] = useState(false);
  const [invoice, setInvoice] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [showLicenseFlow, setShowLicenseFlow] = useState(false);
  const [timezone, setTimezone] = useState("America/New_York");
  const [licenseEndDate, setLicenseEndDate] = useState("");

  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get("session_id");
  const userId = params.get("user_id");

  useEffect(() => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setTimezone(tz || "America/New_York");
  }, []);

  useEffect(() => {
    const checkPaymentRecord = async () => {
      try {
        if (userId && sessionId) {
          if (user.userId === userId) {
            const res = await getPaymentRecord({ userId, sessionId });
            if (res.data.invoice) { setInvoice(res.data.invoice); setPaid(true); }
            else { setPaid(false); }
            if (res.data.licenseKeys) setLicenseKeys(res.data.licenseKeys);
          }
        } else {
          const res = await getPaymentRecord({ userId: user.userId });
          if (res.data.invoice) { setInvoice(res.data.invoice); setPaid(true); }
          else if (res.data.isVerified) { setVerified(true); setLicenseEndDate(res.data.endDate); }
          else { setPaid(false); }
          if (res.data.licenseKeys) setLicenseKeys(res.data.licenseKeys);
        }
      } catch (error) {
        console.error("Error fetching payment record:", error);
      }
    };
    checkPaymentRecord();
  }, [sessionId, userId, user]);

  const handleAccept = async () => {
    try {
      setLoading(true);
      const response = await createCheckoutSession({ user, timezone });
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Error checkout:", error);
    } finally {
      setLoading(false);
    }
  };

  const MembershipCard = ({ planName, description, price }) => {
    const startDate = new Date().toLocaleDateString();
    const endDate = new Date();
    endDate.setFullYear(endDate.getFullYear() + 1);
    const descriptionLines = description.split("\n");
    return (
      <div className="membership-card">
        <div className="header"><h2>{planName}</h2></div>
        <ul className="description">
          {descriptionLines.map((line, index) => <li key={index}>{line}</li>)}
        </ul>
        <div>
          <span className="price">{`${price}/${t("paymentpage.plan.month")}`}</span>
          <span>{t("paymentpage.plan.minimum")}</span>
        </div>
        {!licenseEndDate && (
          <div className="dates">
            <div className="date-item"><h5>{t("paymentpage.plan.from")}</h5> <span>{startDate}</span></div>
            <div className="date-item"><h5>{t("paymentpage.plan.to")}</h5> <span>{endDate.toLocaleDateString()}</span></div>
          </div>
        )}
      </div>
    );
  };

  let SallieText = paid
    ? t("paymentpage.sallie.paid")
    : verified
      ? `You have been verified! Your membership end date is ${licenseEndDate}.`
      : t("paymentpage.sallie.default");

  const membershipPlans = [
    {
      planName: t("paymentpage.plan.name"),
      description: t("paymentpage.plan.description"),
      price: "$99",
    },
  ];

  const handleDownloadInvoice = () => { window.location.href = invoice.invoice_pdf; };

  const handleStartFreeTrial = () => {
    startFreeTrial({ userId: user.userId })
      .then(() => { history.push("/patientControl"); })
      .catch((error) => { console.error("Error starting free trial:", error); });
  };

  function getEndDateString(effectiveAtTimestamp) {
    const effectiveDate = new Date(effectiveAtTimestamp * 1000);
    const endDate = new Date(effectiveDate.getFullYear() + 1, effectiveDate.getMonth(), effectiveDate.getDate());
    return endDate.toLocaleDateString();
  }

  return (
    <>
      <OnboardingStepHeader text={t("paymentpage.header")} />
      <Sallie text={SallieText} />

      <h4 className="mt-4 mb-4">Subscription Details</h4>
      <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap", margin: "0.75rem 0" }}>
        <div
          onClick={handleStartFreeTrial}
          style={{ flex: 1, minWidth: "220px", border: "2px solid #1c3f6e", borderRadius: "8px", padding: "1rem", background: "#eaf2fa", cursor: "pointer" }}
        >
          <h5 style={{ marginBottom: "0.5rem" }}>Free Trial</h5>
          <ul style={{ listStyle: "none", paddingLeft: 0, margin: 0 }}>
            <li style={{ marginBottom: "0.4rem" }}>
              <span style={{ color: "#16a34a", fontWeight: 700, marginRight: "0.5rem" }}>✓</span>
              Patient Referrals
            </li>
            <li style={{ marginBottom: "0.4rem" }}>
              <span style={{ color: "#16a34a", fontWeight: 700, marginRight: "0.5rem" }}>✓</span>
              1 free Report upon Patient's Onboarding
            </li>
            <li style={{ marginBottom: "0.4rem" }}>
              <span style={{ color: "#dc2626", fontWeight: 700, marginRight: "0.5rem" }}>✗</span>
              Access to your patient's Dashboard
            </li>
            <li>
              <span style={{ color: "#dc2626", fontWeight: 700, marginRight: "0.5rem" }}>✗</span>
              Access to your patient's full Reports page
            </li>
          </ul>
        </div>
        <div
          onClick={() => setShowLicenseFlow(true)}
          style={{ flex: 1, minWidth: "220px", border: "2px solid #1c3f6e", borderRadius: "8px", padding: "1rem", background: "#eaf2fa", cursor: "pointer" }}
        >
          <h5 style={{ marginBottom: "0.5rem" }}>License Key</h5>
          <ul style={{ listStyle: "none", paddingLeft: 0, margin: 0 }}>
            <li style={{ marginBottom: "0.4rem" }}>
              <span style={{ color: "#16a34a", fontWeight: 700, marginRight: "0.5rem" }}>✓</span>
              Access to your patient's Dashboard
            </li>
            <li style={{ marginBottom: "0.4rem" }}>
              <span style={{ color: "#16a34a", fontWeight: 700, marginRight: "0.5rem" }}>✓</span>
              Access to your patient's full Reports page
            </li>
            <li>
              <span style={{ color: "#16a34a", fontWeight: 700, marginRight: "0.5rem" }}>✓</span>
              Patient Referrals
            </li>
          </ul>
        </div>
      </div>
      <hr style={{ border: "none", borderTop: "1px solid #e0e0e0", margin: "1.5rem 0" }} />

      {!paid && !verified && showLicenseFlow && (
        <LicenseKeyInput user={user} setVerified={setVerified} setLicenseEndDate={setLicenseEndDate} />
      )}
      {!paid && !verified && showLicenseFlow && (
        <hr style={{ border: "none", borderTop: "1px solid #e0e0e0", margin: "1.5rem 0" }} />
      )}
      {!paid && !verified && showLicenseFlow && (
        <div>
          {membershipPlans.map((plan) => (
            <MembershipCard key={plan.planName} planName={plan.planName} description={plan.description} price={plan.price} />
          ))}
        </div>
      )}
      {verified && membershipPlans.map((plan) => (
        <MembershipCard key={plan.planName} planName={plan.planName} description={plan.description} price={plan.price} />
      ))}
      {paid && (
        <>
          <div className="invoice-card">
            <div className="invoice-container">
              <h2>{t("paymentpage.invoice.title")}</h2>
              <p>{t("paymentpage.invoice.number")} {invoice.number}</p>
              <p>{t("paymentpage.invoice.amount")} ${(invoice.amount_due / 100).toFixed(2)} USD</p>
              <p>{t("paymentpage.invoice.name")} {invoice.customer_name}</p>
              <p>{t("paymentpage.invoice.email")} {invoice.customer_email}</p>
              <p>{t("paymentpage.invoice.effective")} {new Date(invoice.effective_at * 1000).toLocaleDateString()}</p>
              <p>{t("paymentpage.invoice.end")} {getEndDateString(invoice.effective_at)}</p>
              <p className="status">{t("paymentpage.invoice.status")} {invoice.status}</p>
            </div>
          </div>
          {licenseKeys.length > 0 && (
            <div className="invoice-card">
              <div className="invoice-container">
                <h2>{t("paymentpage.license.title")}</h2>
                <ul>
                  {licenseKeys.map((key, index) => <li key={index}><span>{key}</span></li>)}
                </ul>
              </div>
            </div>
          )}
        </>
      )}

      {paid ? (
        <div className="d-flex justify-content-end">
          <SubmitButton onClick={handleDownloadInvoice}>{t("paymentpage.invoice.download")}</SubmitButton>
          <SubmitButton onClick={() => history.push("/signIn")}>{t("paymentpage.go_to_dashboard")}</SubmitButton>
        </div>
      ) : verified ? (
        <SubmitButton onClick={() => history.push("/signIn")}>{t("paymentpage.go_to_dashboard")}</SubmitButton>
      ) : (
        showLicenseFlow && (
          <div className="d-flex justify-content-end">
            <div className="sallie mr-4" style={{ width: "70%" }}>
              <h6>{t("paymentpage.payment.interest")}</h6>
            </div>
            <div className="d-flex justify-content-end">
              <SubmitButton onClick={() => setShowModal(true)}>{t("paymentpage.payment.submit")}</SubmitButton>
            </div>
          </div>
        )
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} dialogClassName="custom-modal">
        <Modal.Header closeButton>
          <Modal.Title>{t("paymentpage.modal.title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modal-scroll">
          <PrivacyPage fromPaymentpage={true} />
        </Modal.Body>
        <Modal.Footer>
          {!loading && (
            <SubmitButton variant="secondary" onClick={() => setShowModal(false)}>
              {t("paymentpage.cancel")}
            </SubmitButton>
          )}
          <SubmitButton variant="primary" onClick={handleAccept} disabled={loading}>
            {t("paymentpage.accept")}
          </SubmitButton>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PaymentPage;